import {
  type Dependency,
  type Task,
  type WorkCalendar,
  calculateSchedule,
  calendarFromSettings,
  rollUpGroups,
  workingDaysInclusive,
} from '@pst/core-scheduling';
import type {
  GroupView,
  PlanView,
  SchedDependency,
  SchedTask,
  ScheduleView,
} from '../../api';

/**
 * The network calculation, run in the browser.
 *
 * This is the reason the project is TypeScript throughout (ADR 0001): the
 * functions called here are the SAME compiled code the server runs. Changing a
 * duration shows the new critical path without a network round trip, and when
 * the server answers, its result agrees - if it ever did not, that would be a
 * bug in one shared function rather than a difference between two
 * implementations.
 *
 * The server remains the authority. This only removes the wait.
 */
export function calendarOf(plan: PlanView): WorkCalendar {
  return calendarFromSettings({
    bundesland: plan.calendar.bundesland,
    workingWeekdays: plan.calendar.workingWeekdays,
    fronleichnam: plan.calendar.fronleichnam,
    mariaeHimmelfahrt: plan.calendar.mariaeHimmelfahrt,
    exceptions: plan.calendar.exceptions,
    referenceDate: plan.startDate,
  });
}

/**
 * Applies a pending edit and recalculates, for immediate display.
 *
 * Returns null when the result cannot be computed - a cycle, say. The preview
 * is then simply not shown and the server reports the real error a moment
 * later, which is better than guessing at an error message locally.
 */
export function previewEdit(
  plan: PlanView,
  current: ScheduleView,
  dependencies: SchedDependency[],
  taskId: string,
  body: Record<string, unknown>,
): ScheduleView | null {
  const calendar = calendarOf(plan);

  const tasks = current.tasks.map((task) => {
    if (task.id !== taskId) return task;
    const next = { ...task };

    if (typeof body.durationDays === 'number') next.durationDays = body.durationDays;
    if (typeof body.name === 'string') next.name = body.name;
    if (body.summaryId === null || typeof body.summaryId === 'string') next.summaryId = body.summaryId as string | null;
    if (body.color === null || typeof body.color === 'string') {
      next.color = body.color as string | null;
    }

    if (body.startDate === null) {
      next.constraintType = null;
      next.constraintDate = null;
    } else if (typeof body.startDate === 'string') {
      next.constraintType = 'startNoEarlierThan';
      next.constraintDate = body.startDate;
    }

    // An end date changes the duration, exactly as the server does it - with
    // the same calendar, so the preview cannot disagree.
    if (typeof body.finishDate === 'string' && task.earlyStart) {
      if (body.finishDate >= task.earlyStart) {
        next.durationDays = workingDaysInclusive(
          calendar,
          task.earlyStart,
          body.finishDate,
        );
      }
    }
    return next;
  });

  const result = recalculate(plan, tasks, dependencies, current.groups, calendar);
  return result && { ...result, summaries: current.summaries };
}

export function recalculate(
  plan: PlanView,
  tasks: SchedTask[],
  dependencies: SchedDependency[],
  groups: GroupView[],
  calendar = calendarOf(plan),
): ScheduleView | null {
  const engineTasks: Task[] = tasks.map((task) =>
    task.constraintType && task.constraintDate
      ? {
          id: task.id,
          durationDays: task.durationDays,
          constraint: { type: task.constraintType, date: task.constraintDate },
        }
      : { id: task.id, durationDays: task.durationDays },
  );

  const engineDependencies: Dependency[] = dependencies.map((dependency) => ({
    predecessorId: dependency.predecessorId,
    successorId: dependency.successorId,
    type: dependency.type,
    lagDays: dependency.lagDays,
  }));

  const result = calculateSchedule({
    tasks: engineTasks,
    dependencies: engineDependencies,
    calendar,
    projectStart: plan.startDate,
    ...(plan.targetFinish ? { targetFinish: plan.targetFinish } : {}),
  });
  if (!result.ok) return null;

  const analysed = result.value.tasks;
  const nextTasks: SchedTask[] = tasks.map((task) => {
    const analysis = analysed.get(task.id);
    if (!analysis) return task;
    return {
      ...task,
      earlyStart: analysis.earlyStart,
      earlyFinish: analysis.earlyFinish,
      lateStart: analysis.lateStart,
      lateFinish: analysis.lateFinish,
      totalFloat: analysis.totalFloat,
      freeFloat: analysis.freeFloat,
      critical: analysis.critical,
    };
  });

  // Phases follow from the tasks, so they are rolled up with the very same
  // function the server uses.
  const rollups = rollUpGroups(
    groups.map((group) => ({ id: group.id, parentId: group.parentId })),
    new Map(tasks.map((task) => [task.id, task.wbsNodeId])),
    new Map(
      nextTasks
        .filter((task) => task.earlyStart !== null && task.earlyFinish !== null)
        .map((task) => [
          task.id,
          {
            earlyStart: task.earlyStart as string,
            earlyFinish: task.earlyFinish as string,
            critical: task.critical === true,
          },
        ]),
    ),
    calendar,
  );

  return {
    projectStart: result.value.projectStart,
    earliestFinish: result.value.earliestFinish,
    targetFinish: result.value.targetFinish,
    behindTarget: result.value.behindTarget,
    criticalTaskIds: [...result.value.criticalTasks],
    calculatedAt: new Date().toISOString(),
    stale: false,
    summaries: [],
    tasks: nextTasks,
    groups: groups.map((group) => {
      const rollup = rollups.get(group.id);
      return rollup
        ? {
            ...group,
            depth: rollup.depth,
            earlyStart: rollup.earlyStart,
            earlyFinish: rollup.earlyFinish,
            durationDays: rollup.durationDays,
            critical: rollup.critical,
            taskCount: rollup.taskCount,
          }
        : group;
    }),
  };
}
